import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { Modal } from '../components/Modal.jsx';

export default function Articles() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const { data, isLoading } = useQuery({ queryKey: ['articles', q], queryFn: () => api(`/api/articles?q=${encodeURIComponent(q)}`) });
  const { data: cats } = useQuery({ queryKey: ['categories'], queryFn: () => api('/api/categories') });
  const { data: vats } = useQuery({ queryKey: ['vat'], queryFn: () => api('/api/vat') });

  const [editing, setEditing] = useState(null);
  const [adjusting, setAdjusting] = useState(null);

  const save = useMutation({
    mutationFn: p => p.id
      ? api(`/api/articles/${p.id}`, { method: 'PATCH', body: p.data })
      : api('/api/articles', { method: 'POST', body: p.data }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['articles'] }); setEditing(null); },
  });
  const adj = useMutation({
    mutationFn: p => api(`/api/articles/${p.id}/adjust`, { method: 'POST', body: p.data }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['articles'] }); setAdjusting(null); },
  });

  return (
    <>
      <div className="page-header">
        <h1>{t('articles.title')}</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <input placeholder={t('common.search')} value={q} onChange={e => setQ(e.target.value)} />
          <button className="btn btn-primary" onClick={() => setEditing({})}>{t('articles.add')}</button>
        </div>
      </div>
      {isLoading ? <div className="card">{t('common.loading')}</div> : (
        <table className="table">
          <thead>
            <tr>
              <th>{t('articles.sku')}</th><th>{t('common.name')}</th><th>{t('articles.kind')}</th>
              <th>{t('articles.category')}</th><th>{t('articles.price')}</th>
              <th>{t('articles.stock')}</th><th>{t('articles.avgCost')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {data?.articles?.map(a => (
              <tr key={a.id}>
                <td><code>{a.sku}</code></td>
                <td>{a.name}</td>
                <td>{a.kind === 'PRODUCT' ? t('articles.product') : t('articles.service')}</td>
                <td>{a.category?.name || '—'}</td>
                <td>{Number(a.price).toFixed(2)}</td>
                <td>
                  {a.kind === 'PRODUCT' ? (
                    <span style={{ color: a.lowStockAt != null && Number(a.stockQty) <= a.lowStockAt ? 'var(--warning)' : undefined }}>
                      {Number(a.stockQty).toFixed(2)} {a.unit}
                    </span>
                  ) : '—'}
                </td>
                <td>{a.kind === 'PRODUCT' ? Number(a.avgCost).toFixed(4) : '—'}</td>
                <td>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditing(a)}>{t('common.edit')}</button>{' '}
                  {a.kind === 'PRODUCT' && (
                    <button className="btn btn-secondary btn-sm" onClick={() => setAdjusting(a)}>{t('articles.adjust')}</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editing && (
        <ArticleModal article={editing} cats={cats?.categories || []} vats={vats?.rates || []} onClose={() => setEditing(null)} onSave={d => save.mutate({ id: editing.id, data: d })} saving={save.isPending} error={save.error?.data} />
      )}
      {adjusting && (
        <AdjustModal article={adjusting} onClose={() => setAdjusting(null)} onSave={d => adj.mutate({ id: adjusting.id, data: d })} saving={adj.isPending} />
      )}
    </>
  );
}

function ArticleModal({ article, cats, vats, onClose, onSave, saving, error }) {
  const { t } = useTranslation();
  const isNew = !article.id;
  const [form, setForm] = useState({
    sku: article.sku || '',
    name: article.name || '',
    description: article.description || '',
    kind: article.kind || 'PRODUCT',
    categoryId: article.categoryId || '',
    unit: article.unit || 'pcs',
    price: article.price != null ? Number(article.price) : 0,
    vatRateId: article.vatRateId || '',
    lowStockAt: article.lowStockAt ?? '',
    barcode: article.barcode || '',
    openingQty: 0, openingCost: 0,
    active: article.active ?? true,
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = e => {
    e.preventDefault();
    const p = { ...form };
    p.categoryId = p.categoryId || null;
    p.vatRateId = p.vatRateId || null;
    p.lowStockAt = p.lowStockAt === '' ? null : Number(p.lowStockAt);
    if (!isNew) { delete p.sku; delete p.openingQty; delete p.openingCost; }
    onSave(p);
  };
  return (
    <Modal title={isNew ? t('articles.add') : t('common.edit')} onClose={onClose} wide>
      <form onSubmit={submit}>
        {error && <div className="error-banner">{error.error}</div>}
        <div className="row">
          {isNew && <div className="field"><label>{t('articles.sku')}</label><input required value={form.sku} onChange={e => set('sku', e.target.value)} /></div>}
          <div className="field"><label>{t('common.name')}</label><input required value={form.name} onChange={e => set('name', e.target.value)} /></div>
        </div>
        <div className="field"><label>{t('articles.description')}</label><textarea value={form.description} onChange={e => set('description', e.target.value)} /></div>
        <div className="row">
          <div className="field"><label>{t('articles.kind')}</label>
            <select value={form.kind} onChange={e => set('kind', e.target.value)} disabled={!isNew}>
              <option value="PRODUCT">{t('articles.product')}</option>
              <option value="SERVICE">{t('articles.service')}</option>
            </select>
          </div>
          <div className="field"><label>{t('articles.category')}</label>
            <select value={form.categoryId} onChange={e => set('categoryId', e.target.value)}>
              <option value="">—</option>
              {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="field"><label>{t('articles.unit')}</label><input value={form.unit} onChange={e => set('unit', e.target.value)} /></div>
        </div>
        <div className="row">
          <div className="field"><label>{t('articles.price')}</label><input type="number" step="0.0001" min={0} required value={form.price} onChange={e => set('price', Number(e.target.value))} /></div>
          <div className="field"><label>{t('articles.vatRate')}</label>
            <select value={form.vatRateId} onChange={e => set('vatRateId', e.target.value)}>
              <option value="">—</option>
              {vats.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
          <div className="field"><label>{t('articles.barcode')}</label><input value={form.barcode} onChange={e => set('barcode', e.target.value)} /></div>
        </div>
        {form.kind === 'PRODUCT' && (
          <>
            <div className="row">
              <div className="field"><label>{t('articles.lowStockAt')}</label><input type="number" min={0} value={form.lowStockAt} onChange={e => set('lowStockAt', e.target.value)} /></div>
              {isNew && (
                <>
                  <div className="field"><label>{t('articles.openingStock')}</label><input type="number" step="0.0001" min={0} value={form.openingQty} onChange={e => set('openingQty', Number(e.target.value))} /></div>
                  <div className="field"><label>{t('articles.openingCost')}</label><input type="number" step="0.0001" min={0} value={form.openingCost} onChange={e => set('openingCost', Number(e.target.value))} /></div>
                </>
              )}
            </div>
          </>
        )}
        {!isNew && (
          <div className="field"><label><input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} /> {t('common.active')}</label></div>
        )}
        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>{t('common.save')}</button>
        </div>
      </form>
    </Modal>
  );
}

function AdjustModal({ article, onClose, onSave, saving }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ qty: 0, unitCost: '', note: '' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <Modal title={`${t('articles.adjust')}: ${article.name}`} onClose={onClose}>
      <form onSubmit={e => {
        e.preventDefault();
        const p = { qty: Number(form.qty), note: form.note };
        if (form.unitCost !== '') p.unitCost = Number(form.unitCost);
        onSave(p);
      }}>
        <p className="muted">Current: {Number(article.stockQty).toFixed(2)} {article.unit} @ avg {Number(article.avgCost).toFixed(4)}</p>
        <div className="field"><label>{t('articles.qty')} (± signed)</label><input type="number" step="0.0001" required value={form.qty} onChange={e => set('qty', e.target.value)} /></div>
        <div className="field"><label>{t('articles.openingCost')} (optional)</label><input type="number" step="0.0001" min={0} value={form.unitCost} onChange={e => set('unitCost', e.target.value)} /></div>
        <div className="field"><label>Note</label><input required value={form.note} onChange={e => set('note', e.target.value)} /></div>
        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>{t('common.save')}</button>
        </div>
      </form>
    </Modal>
  );
}
